import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslator, useUrlLocalizer } from 'tradera-lang';
import ALink from 'tradera-components/alink';
import FilterIcon from '@tradera/blueprint/theme/icons2/filter.svg?jsx';
import TimeIcon from '@tradera/blueprint/theme/icons2/time.svg?jsx';
import UserIcon from '@tradera/blueprint/theme/icons2/user-fill.svg?jsx';
import HighlightWord from './highlight-word';
import CloseNarrowIcon from '@tradera/blueprint/theme/icons/close-narrow.svg?jsx';
import {
    addRecentSearchText,
    removeRecentSearchText
} from 'tradera-state/personalisation/actions';

// Dont blur input when there's a mouseclick on the search suggestions
const preventDefault = evt => {
    evt.preventDefault();
};

// Without this, the search field dropdown is collapsed. Fixes TWT-993.
const fixFlexHeightInIpadMini2Style = { flexBasis: 'auto' };

export const PreviousSearchesListItem = ({
    changeSearchTerm,
    inSPA,
    isSelected,
    onPreviousSearchRemoved,
    searchTerm,
    selectedCategoryId,
    text
}) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const dispatch = useDispatch();
    return (
        <div
            className={`list-group-item list-group-item-action slim-header__search-suggestion-wrapper slim-header__search-suggestion ${
                isSelected ? 'slim-header__search-suggestion--active' : ''
            }`}>
            <ALink
                useLink={inSPA}
                href={toLocalizedUrl(
                    `/search?q=${encodeURIComponent(text)}&categoryId=${
                        selectedCategoryId ? selectedCategoryId : 0
                    }`
                )}
                className={`list-group-item-action slim-header__search-suggestion bg-transparent`}
                onClick={() => {
                    dispatch(addRecentSearchText(text));
                    changeSearchTerm(text);
                }}
                onMouseDown={preventDefault}
                data-suggestion
                data-previous-suggestion={text}
                data-ga-category="User search"
                data-ga-action="Suggestion - Previous Query"
                data-ga-label={searchTerm}>
                <HighlightWord
                    className=""
                    query={searchTerm}
                    text={text}
                    icon={<TimeIcon className="icon align-text-bottom mr-3" />}
                />
            </ALink>
            <button
                aria-label="Remove recent search"
                className="slim-header__clear-button unbutton btn"
                data-search-clear
                onClick={() => {
                    dispatch(removeRecentSearchText(text));
                    onPreviousSearchRemoved();
                }}
                onMouseDown={preventDefault}
                type="reset">
                <CloseNarrowIcon className="icon icon-sm align-middle" />
            </button>
        </div>
    );
};

export const SearchesSuggestionsListItem = ({
    changeSearchTerm,
    inSPA,
    isSelected,
    item,
    searchTerm,
    selectedCategoryId
}) => {
    const categoryId = item.category
        ? item.category.id
        : selectedCategoryId
        ? selectedCategoryId
        : 0;
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <ALink
            useLink={inSPA}
            href={toLocalizedUrl(
                `/search?q=${encodeURIComponent(
                    item.text
                )}&categoryId=${categoryId}`
            )}
            className={`list-group-item list-group-item-action slim-header__search-suggestion ${
                isSelected ? 'slim-header__search-suggestion--active' : ''
            }`}
            onClick={() => changeSearchTerm(item.text)}
            onMouseDown={preventDefault}
            data-suggestion
            data-search-suggestion={item.text}
            data-ga-category="User search"
            data-ga-action={`Suggestion - ${
                item.category ? 'Category' : 'Auto Query'
            }`}
            data-ga-label={item.text}>
            <HighlightWord query={searchTerm} text={item.text} />
            <i> {item.category ? `inom ${item.category.name}` : ''}</i>
        </ALink>
    );
};

export const ShopsSuggestionsListItem = ({
    changeSearchTerm,
    // inSPA,
    isSelected,
    item,
    searchTerm
}) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const suggestionUrl =
        item.name === 'Musikhjälpen'
            ? toLocalizedUrl('/charity/musikhjalpen')
            : toLocalizedUrl(`/profile/items/${item.memberId}/${item.urlName}`);
    return (
        <ALink
            useLink={false} // this will force page to reload. bug happens making the header not load if we don't do this. once full profile page is in react then we can make this attribute dynamic again
            href={suggestionUrl}
            className={`list-group-item list-group-item-action slim-header__search-suggestion ${
                isSelected ? 'slim-header__search-suggestion--active' : ''
            }`}
            onClick={() => changeSearchTerm(item.text)}
            onMouseDown={preventDefault}
            data-suggestion
            data-shop-suggestion={item.name}
            data-ga-category="User search"
            data-ga-action="Suggestion - Shop"
            data-ga-label={item.name}>
            <em className="badge badge-primary mr-2">{t('siteHeader_shop')}</em>
            <HighlightWord query={searchTerm} text={item.name} />
        </ALink>
    );
};

export const CampaignSuggestionsListItem = ({
    changeSearchTerm,
    // inSPA,
    isSelected,
    item,
    searchTerm
}) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const suggestionUrl = toLocalizedUrl('/charity/' + item.campaignId);
    return (
        <ALink
            useLink={false} // this will force page to reload. bug happens making the header not load if we don't do this. once full profile page is in react then we can make this attribute dynamic again
            href={suggestionUrl}
            className={`list-group-item list-group-item-action slim-header__search-suggestion ${
                isSelected ? 'slim-header__search-suggestion--active' : ''
            }`}
            onClick={() => changeSearchTerm(item.text)}
            onMouseDown={preventDefault}
            data-suggestion
            data-campaign-suggestion={item.headline}
            data-ga-category="User search"
            data-ga-action="Suggestion - Campaign"
            data-ga-label={item.headline}>
            <em className="badge badge-primary mr-2">
                {t('siteHeader_campaign')}
            </em>
            <HighlightWord query={searchTerm} text={item.headline} />
        </ALink>
    );
};

export const AdvancedSearchListItem = ({ isSelected }) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <a
            href={toLocalizedUrl('/search/detailed')}
            data-suggestion
            data-advanced-search
            data-ga-category="User search"
            data-ga-action="Link - Advanced search"
            className={`list-group-item list-group-item-action slim-header__search-advanced underline slim-header__search-suggestion col-12 ${
                isSelected ? 'slim-header__search-suggestion--active' : ''
            }`}
            style={fixFlexHeightInIpadMini2Style}
            onMouseDown={preventDefault}>
            <FilterIcon className="icon align-text-bottom" />
            <span className="ml-3">{t('siteHeader_searchAdvanced')}</span>
        </a>
    );
};

export const SellerSearchListItem = ({ isSelected }) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <a
            href={toLocalizedUrl(
                '/search/detailed/member?itm_source=nav&itm_medium=search-detailed-member'
            )}
            data-suggestion
            data-seller-search
            data-ga-category="User search"
            data-ga-action="Link - Username search"
            className={`list-group-item list-group-item-action slim-header__search-seller underline slim-header__search-suggestion col-12 ${
                isSelected ? 'slim-header__search-suggestion--active' : ''
            }`}
            style={fixFlexHeightInIpadMini2Style}
            onMouseDown={preventDefault}>
            <UserIcon className="icon align-text-bottom" />
            <span className="ml-3">{t('siteHeader_searchSeller')}</span>
        </a>
    );
};
